import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AppLayout } from "../shared/layout";
import { AgencyCard, SearchView } from "../../../components";
import {
  deleteAgency,
  getAgencies,
  uploadAgencyWithExcel,
} from "../../../data";
import ListView from "../../../components/list-view";
import SelectableListView from "../../../components/selectable-list-view";
import { useTableView } from "@/hooks/useTableView";
import { Button } from "@/components/ui/button";
import { countryOptions } from "@/utils/country-options";

export function AgencyListScreen() {
  const { isAdvancedTable } = useTableView();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("");
  const [searchParams, setSearchParams] = useState({
    name: "",
    country: "",
  });
  const [queryParams, setQueryParams] = useState({
    name: "",
    country: "",
  });

  const searchTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isProcess, setIsProcess] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["agencies", page, pageSize, queryParams, sortColumn, sortDirection],
    queryFn: () =>
      getAgencies({
        page,
        pageSize,
        nameFilter: queryParams.name,
        countryFilter: queryParams.country,
        sortColumn,
        sortDirection,
      }),
  });

  const fileSelect = React.useRef(null);

  const pageSettings = {
    title: "Agency List",
    actions: [
      {
        name: "Excel Upload",
        color: "bg-red-100 hover:bg-red-400 hover:text-black text-red-800",
        icon: "add-icon",
        onClick: () => {
          fileSelect.current.click();
        },
      },
      {
        name: "Create New Agency",
        color: "bg-blue-500 hover:bg-blue-700 text-white",
        icon: "add-icon",
        onClick: () => {
          navigate("/agencies/add");
        },
      },
    ],
  };

  const goToEdit = (id) => () => {
    window.location.href = `/agencies/${id}`;
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    const formData = new FormData();
    formData.append("file", file);
    setIsProcess(true);

    uploadAgencyWithExcel(formData).then(() => {
      setIsProcess(false);
      fileSelect.current.value = "";
      queryClient.invalidateQueries("agencies");
    });
  };

  const mutation = useMutation({
    mutationKey: ["deleteAgency"],
    mutationFn: (data) => deleteAgency(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["agencies"]);
    },
  });

  const onDeleteAgency = (id) => {
    mutation.mutate(id);
  };

  const handleSearch = (item) => {
    const newSearchParams = { ...searchParams, [item.key]: item.value };
    setSearchParams(newSearchParams);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setQueryParams(newSearchParams);
      setPage(1);
    }, 300);
  };

  // Eski tablo için basit kolonlar
  const simpleColumns = [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "country", label: "Country" },
    { key: "clientCount", label: "Client Count" },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <div>
          <Button onClick={goToEdit(row.id)}>Edit</Button>
        </div>
      ),
    },
  ];

  // Gelişmiş tablo için tüm kolonlar
  const advancedColumns = [
    { key: "id", label: "ID", sortable: true },
    { key: "name", label: "Name", sortable: true },
    { key: "email", label: "Email", sortable: true },
    { key: "phoneNumber", label: "Phone Number", sortable: true },
    { key: "country", label: "Country", sortable: true },
    { key: "currency", label: "Currency", sortable: true },
    { key: "bankName", label: "Bank Name", sortable: true },
    { key: "IBAN", label: "IBAN", sortable: false },
    { key: "lucaId", label: "Luca ID", sortable: true },
    { key: "lucaAccountName", label: "Luca Account Name", sortable: true },
    { key: "newTvAccountManager", label: "NewTV Manager", sortable: true },
    { key: "clientCount", label: "Client Count", sortable: false },
    {
      key: "socialMediaAccount",
      label: "Social Media",
      sortable: false,
      accessor: (item) => item.socialMediaAccount?.map(acc => `${acc.platform}: ${acc.accountId}`).join(", ") || "-"
    },
    {
      key: "actions",
      label: "Actions",
      sortable: false,
      render: (row) => (
        <div>
          <Button size="sm" onClick={goToEdit(row.id)}>Edit</Button>
        </div>
      ),
    },
  ];

  const defaultVisibleColumns = ["name", "email", "country", "lucaId", "clientCount", "actions"];

  const handleSort = (column, direction) => {
    setSortColumn(column);
    setSortDirection(direction);
  };

  return (
    <AppLayout
      title={pageSettings.title}
      actions={pageSettings.actions}
      loading={isLoading || isProcess}
    >
      <input
        ref={fileSelect}
        hidden
        type="file"
        onChange={handleFileChange}
        className="mb-4"
      />

      <SearchView
        searchParams={searchParams}
        handleSearch={handleSearch}
        countryOptions={countryOptions}
      />

      {isAdvancedTable ? (
        <SelectableListView
          isLoading={isLoading}
          data={data?.data.agencies}
          columns={advancedColumns}
          columnVisibilityKey="agency-list-columns"
          defaultVisibleColumnKeys={defaultVisibleColumns}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSortChange={handleSort}
          totalItems={data?.data.totalItems}
          enablePagination={true}
          itemsPerPage={pageSize}
          currentPage={page}
          onPageChange={setPage}
          onRowClick={(item) => navigate(`/agencies/${item.id}`)}
        />
      ) : (
        <ListView
          isLoading={isLoading}
          data={data?.data.agencies}
          columns={simpleColumns}
          totalItems={data?.data.totalItems}
          enablePagination={true}
          itemsPerPage={pageSize}
          currentPage={page}
          onPageChange={setPage}
          onRowClick={(item) => navigate(`/agencies/${item.id}`)}
          renderGridItem={(item) => (
            <AgencyCard
              key={item.id}
              name={item.name}
              email={item.email}
              phoneNumber={item.phoneNumber}
              country={item.country}
              currency={item.currency}
              bankName={item.bankName}
              IBAN={item.IBAN}
              lucaId={item.lucaId}
              lucaAccountName={item.lucaAccountName}
              newTvAccountManager={item.newTvAccountManager}
              clientCount={item.clientCount}
              socialMediaAccount={item.socialMediaAccount}
              editClick={goToEdit(item.id)}
            />
          )}
        />
      )}
    </AppLayout>
  );
}
